<template>
    <Dialog
        :value="true"
        :header-text="$t('Email settings')"
        :width="700"
        @close_modal="$emit('close')"
    >
        <div class="mt-4">
            <dropdown
                :items="mappedEmails"
                :selected="mappedEmails.filter(x => selectedEmailIntegrationIdsCopy.includes(x.id))"
                :label="$t('Emails')"
                name-key="mappedName"
                value-key="id"
                show-selected-first
                multi
                deselect
                @input="selectedEmailIntegrationIdsCopy = $event.map(x => x.id)"
            />
            <div class="body-2 mt-6 mb-3">{{ $t('Or connect new email address') }}</div>
            <IntegrationButton
                v-for="(button, index) in EmailIntegrationModule.getIntegrations(companyId)"
                :key="index"
                :img="button.img"
                :name="button.name"
                @click="integrationSelected(button.link)"
            />
            <div class="body-2 primary--text mt-4 cursor-pointer" @click="openSmtp">{{ $t('Connect with Microsoft Exchange or other email server (SMTP)') }}</div>
        </div>
        <template slot="footer">
            <v-spacer />
            <primary-button @click="onSave">
                {{ $t("Save") }}
            </primary-button>
        </template>
        <EditEmailingEmailDialog
            v-if="emailDialogData"
            :email="emailDialogData"
            :errors="errors"
            :company-id="companyId"
            default-for-client
            hide-delete
            hide-reconnect
            disable-visibility
            @close="onClose"
            @save="onSaveEmail"
            @send-test-email="sendTestEmail($event)"
        />
    </Dialog>
</template>

<script>
import Dialog from "../../../components/Dialog.vue";
import {computed, onMounted, onUnmounted, reactive, ref} from "@vue/composition-api";
import ApiEmailAutomationEmail from '@/api/email_automation/email_integration'
import { $t } from "@/i18n";
import { get, set } from '@/helpers/get-set'
import AccountModule from "@/store/modules/account";
import IntegrationButton from "@/components/Buttons/IntegrationButton";
import ApiEmailAutomationEmails from "@/api/email_automation/email_integration";
import WS from "@/utility/webSocket";
import EditEmailingEmailDialog from "@/components/Dialog/EmailIntegration/Index";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import EmailIntegrationModule from "@/modules/emailIntegration/emailIntegration"
import ApiEmailIntegration from "@/api/email_automation/email_integration";
import DialogModule from "@/store/modules/dialog";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";

export default {
    name: "EmailsFieldAddDialog",
    components: {
        IntegrationButton,
        Dialog,
        EditEmailingEmailDialog
    },
    props: {
        emails: {
            type: Array,
            required: true
        },
        selectedEmailIntegrationIds: {
            type: Array,
            required: true
        },
        companyId: {
            type: Number,
            required: true
        }
    },
    setup(props, ctx) {
        const smtpVisible = ref(false);
        const editEmail = ref(null);
        const errors = ref(null);
        const selectedEmailIntegrationIdsCopy = ref([]);
        const dialogModule = DialogModule();
        const { showFeedbackNotification } = FeedbackNotificationModule();
        const defaultSmtpData = ref(null);
        const { current_account } = AccountModule()
        const clientDefaultEmailIntegrationId = ref(null);
        selectedEmailIntegrationIdsCopy.value = props.selectedEmailIntegrationIds.slice()

        const onWsMessage = async (data) => {
            if (data.email) {
                editEmail.value = data.email
                ctx.emit('add-new-email', data.email)
                selectedEmailIntegrationIdsCopy.value.push(data.email.id)
            }
        }

        const webSocket = reactive(new WS(onWsMessage, '/ws/email_integration'))

        onMounted(async () => {
            webSocket.connect()
            const smtpData = await ApiEmailAutomationEmail.getDefaultSmtpData(props.companyId)
            defaultSmtpData.value = smtpData.default_smtp
        })

        onUnmounted(() => {
            webSocket.close()
        })

        const openIntegrationLink = (service) => {
            let link

            if (service === 'gmail') {
                link = `/api/email_automation/gmail/get_token/${props.companyId}?visibility=client`
            } else if (service === 'outlook') {
                link = `/api/email_automation/outlook/get_token/${props.companyId}?visibility=client`
            }

            window.open(window.location.origin + link, '_blank').focus();
        }

        const openSmtp = () => {
            errors.value = null
            smtpVisible.value = true
        }


        const emailDialogData = computed(() => {
            if (smtpVisible.value) {
                return {
                    integration_type: 'smtp',
                    visibility: 'client',
                    smtp: {
                        email_server: defaultSmtpData.value ? defaultSmtpData.value.email_server : 'other',
                        password: defaultSmtpData.value ? defaultSmtpData.value.password : '',
                        host: defaultSmtpData.value ? defaultSmtpData.value.host : '',
                        port: defaultSmtpData.value ? defaultSmtpData.value.port : '',
                        security: defaultSmtpData.value ? defaultSmtpData.value.security : '',
                        default_smtp_server_for_company: false,
                    },
                    email: '',
                    sender_name: '',
                    reply_to_email: '',
                    default_address: '',
                    owner_id: current_account.value.id
                }
            } else if (editEmail.value) {
                return editEmail.value
            }
        })

        const onSaveEmail = async (email) => {
            try {
                const data = await ApiEmailAutomationEmails.saveEmailIntegration(
                    email.id,
                    email.integration_type,
                    email.visibility,
                    email.email,
                    email.reply_to_email,
                    email.sender_name,
                    email.owner_id,
                    props.companyId,
                    email.default_address,
                    email.smtp
                )

                ctx.emit('add-new-email', data.email)

                if (email.integration_type === 'smtp') {
                    selectedEmailIntegrationIdsCopy.value.push(data.email.id)
                }

                if (email.client_default_address) {
                    clientDefaultEmailIntegrationId.value = data.email.id
                }

                defaultSmtpData.value = data.default_smtp

                smtpVisible.value = false
                editEmail.value = null
            } catch (e) {
                if (e.response.data.error_type === 'form_validation') {
                    errors.value = e.response.data.errors
                } else {
                    errorDialog.showError(e)
                }
            }
        }

        const onClose = () => {
            smtpVisible.value = false
            editEmail.value = null
        }

        const integrationSelected = (link) => {
            window.open(window.location.origin + link + '?visibility=client', '_blank').focus();
        }

        const sendTestEmail = async (email) => {
            try {
                await ApiEmailIntegration.sendTestEmail(
                    email.id,
                    email.integration_type,
                    props.companyId,
                    email.smtp ? email.smtp.host : null,
                    email.smtp ? email.smtp.port : null,
                    email.email,
                    email.smtp ? email.smtp.password : null,
                    email.smtp ? email.smtp.security : null,
                    email.sender_name
                )

                showFeedbackNotification($t('Test email sent'))
            } catch (e) {
                if (e.response.data.error_type === 'form_validation') {
                    if (e.response.data.errors[0].field === 'global') {
                        errorDialog.showError(e)
                    }

                    errors.value = e.response.data.errors
                } else {
                    errorDialog.showError(e)
                }
            }
        }

        const mappedEmails = computed(() => {
            return props.emails.map(emailClone => {
                let email = { ...emailClone }

                if (!email.sender_name) {
                    email.mappedName = email.email
                } else {
                    email.mappedName = `${email.sender_name} (${email.email})`
                }

                return email
            })
        })

        const onSave = () => {
            ctx.emit('save', {
                emailIntegrationIds: selectedEmailIntegrationIdsCopy.value,
                clientDefaultAddressEmailIntegrationId: clientDefaultEmailIntegrationId.value
            })
        }

        return {
            selectedEmailIntegrationIdsCopy,
            openIntegrationLink,
            openSmtp,
            emailDialogData,
            smtpVisible,
            onSave,
            onSaveEmail,
            EmailIntegrationModule,
            integrationSelected,
            onClose,
            errors,
            sendTestEmail,
            mappedEmails
        }
    }
};
</script>